'use client'

import { useTheme } from 'next-themes'
import { Sun, Moon, MonitorCog } from 'lucide-react'
import { useEffect, useState } from 'react'

interface ThemeToggleProps {
  variant?: 'default' | 'condensed'
  direction?: 'horizontal' | 'vertical'
}

export default function ThemeToggle({ variant = 'default', direction = 'horizontal' }: ThemeToggleProps): JSX.Element {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  if (!mounted) {
    return <div className="w-5 h-5" />
  }

  const renderButton = (themeName: string, icon: JSX.Element, ariaLabel: string): JSX.Element => (
    <button
      onClick={() => setTheme(themeName)}
      className={`py-1 px-2 rounded-full text-sm ${theme === themeName ? 'border border-orange-900/70 dark:border-orange-200 text-orange-900/70 dark:text-orange-200 shadow-lg font-light' : 'font-extralight dark:text-gray-400 text-gray-500'}`}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  )

  const getCurrentThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="inline-block" strokeWidth={1} size={16} />
      case 'dark':
        return <Moon className="inline-block" strokeWidth={1} size={16} />
      default:
        return <MonitorCog className="inline-block" strokeWidth={1} size={16} />
    }
  }

  if (variant === 'condensed') {
    return (
      <div className="group relative">
        <div className={`flex ${direction === 'horizontal' ? 'space-x-2' : 'flex-col space-y-2'} border dark:border-gray-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute right-0 z-10`}>
          {renderButton('light', <Sun className="inline-block" strokeWidth={1} size={16} />, "Set theme to light")}
          {renderButton('system', <MonitorCog className="inline-block" strokeWidth={1} size={16} />, "Set theme to system")}
          {renderButton('dark', <Moon className="inline-block" strokeWidth={1} size={16} />, "Set theme to dark")}
        </div>
        <button
          onClick={() => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')}
          className="py-1 px-2 rounded-full text-sm border dark:border-gray-400 text-orange-900/70 dark:text-orange-200 group-hover:opacity-0 transition-opacity duration-200"
          aria-label="Toggle theme"
        >
          {getCurrentThemeIcon()}
        </button>
      </div>
    )
  }

  return (
    <div className={`flex ${direction === 'horizontal' ? 'space-x-2' : 'flex-col space-y-2'} border dark:border-gray-400 rounded-full`}>
      {renderButton('light', <Sun className="inline-block" strokeWidth={1} size={16} />, "Set theme to light")}
      {renderButton('system', <MonitorCog className="inline-block" strokeWidth={1} size={16} />, "Set theme to system")}
      {renderButton('dark', <Moon className="inline-block" strokeWidth={1} size={16} />, "Set theme to dark")}
    </div>
  )
} 